import React from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  Chip,
  Grid
} from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import DashboardModuleCard from './DashboardModuleCard';
import DashboardModuleListItem from './DashboardModuleListItem';

/**
 * Accordion section wrapper for dashboard modules
 * Displays modules in either card or list view based on viewMode
 */
const DashboardSection = ({
  id,
  title,
  icon: Icon,
  expanded,
  onToggle,
  badgeCount = 0,
  viewMode = 'card',
  modules = [],
  onModuleClick,
  pinnedModules = [],
  onPinToggle,
  badgeCounts = {}
}) => {
  if (modules.length === 0) {
    return null;
  }

  return (
    <Accordion
      id={id}
      expanded={expanded}
      onChange={onToggle}
      sx={{
        mb: 2,
        '&:before': { display: 'none' },
        borderRadius: 2,
        overflow: 'hidden',
        boxShadow: expanded ? 2 : 1,
        transition: 'box-shadow 0.2s ease'
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMore />}
        sx={{
          bgcolor: expanded ? 'primary.light' : 'grey.50',
          '&:hover': { bgcolor: expanded ? 'primary.light' : 'grey.100' },
          transition: 'background-color 0.2s ease',
          minHeight: 64,
          '& .MuiAccordionSummary-content': {
            alignItems: 'center'
          }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
          {Icon && (
            <Icon
              sx={{
                color: expanded ? 'primary.dark' : 'text.secondary',
                fontSize: 28
              }}
            />
          )}
          <Typography
            variant="h6"
            sx={{
              flex: 1,
              fontWeight: expanded ? 600 : 500,
              color: expanded ? 'primary.dark' : 'text.primary'
            }}
          >
            {title}
          </Typography>

          {/* Show badge count when collapsed and has badges */}
          {!expanded && badgeCount > 0 && (
            <Chip
              label={badgeCount}
              color="primary"
              size="small"
              sx={{ mr: 2 }}
            />
          )}

          <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
            {modules.length} {modules.length === 1 ? 'module' : 'modules'}
          </Typography>
        </Box>
      </AccordionSummary>

      <AccordionDetails sx={{ p: 3, bgcolor: 'background.paper' }}>
        {viewMode === 'card' ? (
          <Grid container spacing={2}>
            {modules.map(module => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={module.id}>
                <DashboardModuleCard
                  module={module}
                  onClick={() => onModuleClick(module)}
                  isPinned={pinnedModules.includes(module.id)}
                  onPinToggle={() => onPinToggle(module.id)}
                  badge={module.badgeKey ? badgeCounts[module.badgeKey] : null}
                />
              </Grid>
            ))}
          </Grid>
        ) : (
          <Box>
            {modules.map(module => (
              <DashboardModuleListItem
                key={module.id}
                module={module}
                onClick={() => onModuleClick(module)}
                isPinned={pinnedModules.includes(module.id)}
                onPinToggle={() => onPinToggle(module.id)}
                badge={module.badgeKey ? badgeCounts[module.badgeKey] : null}
              />
            ))}
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export default DashboardSection;
